/**
 * Audit Logs - Super-Admin only
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { ShieldAlert } from "lucide-react";
import { SuperAdminGuard } from "../components/SuperAdminGuard";
import { PageHeader } from "../components/PageHeader";
import { useDateFormat } from "../hooks/useDateFormat";
import { Input, Button, Skeleton } from "@jourfx/ui";
import { useSession } from "../lib/auth";

interface AuditLogItem {
  _id: string;
  action: string;
  resource: string;
  resourceId?: string;
  actorEmail?: string;
  details?: string;
  timestamp: number;
}

interface AuditLogsResponse {
  items: AuditLogItem[];
  total: number;
  nextCursor?: number | null;
}

function AuditLogsContent() {
  const { t } = useTranslation("auditLogs");
  const { t: tc } = useTranslation("common");
  const { formatShortDate, formatShortTime } = useDateFormat();
  const { isSuperAdmin } = useSession();

  const [actionFilter, setActionFilter] = useState("");
  const [resourceFilter, setResourceFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState<string>("all");
  const [cursor, setCursor] = useState<number | null>(0);

  const organizationsResult = useQuery(
    api.admin.organizations.list,
    isSuperAdmin ? {} : "skip"
  ) as { organizations?: { _id: string; name: string }[] } | undefined;

  const auditLogs = useQuery(api.admin.auditLogs.list, {
    organizationId: isSuperAdmin && orgFilter !== "all" ? (orgFilter as any) : undefined,
    action: actionFilter || undefined,
    resource: resourceFilter || undefined,
    actor: actorFilter || undefined,
    limit: 50,
    cursor: cursor ?? 0,
  }) as AuditLogsResponse | null | undefined;

  const logItems: AuditLogItem[] = auditLogs?.items ?? [];

  const formatTimestamp = (timestamp: number) =>
    `${formatShortDate(timestamp)} ${formatShortTime(timestamp)}`;

  const resourceOptions = useMemo(() => {
    const set = new Set<string>();
    logItems.forEach((log) => set.add(log.resource));
    return Array.from(set);
  }, [logItems]);

  const actionOptions = useMemo(() => {
    const set = new Set<string>();
    logItems.forEach((log) => set.add(log.action));
    return Array.from(set);
  }, [logItems]);

  if (auditLogs === undefined) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (auditLogs === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <ShieldAlert className="h-12 w-12 text-warning" />
        <h2 className="text-xl font-semibold text-foreground">{tc("auth.loginRequired")}</h2>
        <p className="text-muted-foreground text-center max-w-md">
          {tc("auth.loginRequiredMessage")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("subtitle")}
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        {isSuperAdmin && (
          <select
            value={orgFilter}
            onChange={(e) => {
              setOrgFilter(e.target.value);
              setCursor(0);
            }}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <option value="all">{t("filters.allOrganizations")}</option>
            {(organizationsResult?.organizations || []).map((org) => (
              <option key={org._id} value={org._id}>
                {org.name}
              </option>
            ))}
          </select>
        )}
        <select
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value);
            setCursor(0);
          }}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <option value="">{t("filters.allActions")}</option>
          {actionOptions.map((action) => (
            <option key={action} value={action}>
              {action}
            </option>
          ))}
        </select>
        <select
          value={resourceFilter}
          onChange={(e) => {
            setResourceFilter(e.target.value);
            setCursor(0);
          }}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <option value="">{t("filters.allResources")}</option>
          {resourceOptions.map((resource) => (
            <option key={resource} value={resource}>
              {resource}
            </option>
          ))}
        </select>
        <Input
          value={actorFilter}
          onChange={(e) => {
            setActorFilter(e.target.value);
            setCursor(0);
          }}
          placeholder={t("filters.actorPlaceholder")}
          className="h-10 w-64"
        />
        <Button variant="outline" onClick={() => {
          setActionFilter("");
          setResourceFilter("");
          setActorFilter("");
          setOrgFilter("all");
          setCursor(0);
        }}>
          {t("filters.reset")}
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("table.time")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("table.action")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("table.resource")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("table.actor")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("table.details")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {logItems.map((log) => (
              <tr key={log._id} className="hover:bg-muted/50">
                <td className="px-6 py-4 text-sm text-muted-foreground">
                  {formatTimestamp(log.timestamp)}
                </td>
                <td className="px-6 py-4 text-sm text-foreground">{log.action}</td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {log.resource}
                  {log.resourceId ? (
                    <div className="text-xs text-muted-foreground">{log.resourceId}</div>
                  ) : null}
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {log.actorEmail || "-"}
                </td>
                <td className="px-6 py-4 text-xs text-muted-foreground">
                  {log.details ? log.details : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t("pagination.entries", { count: auditLogs.total })}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setCursor(Math.max(0, (cursor ?? 0) - 50))}
            disabled={(cursor ?? 0) === 0}
          >
            {t("pagination.previous")}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              if (auditLogs.nextCursor != null) {
                setCursor(auditLogs.nextCursor);
              }
            }}
            disabled={auditLogs.nextCursor == null}
          >
            {t("pagination.next")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AuditLogs() {
  return (
    <SuperAdminGuard>
      <AuditLogsContent />
    </SuperAdminGuard>
  );
}
